import { useState } from 'react';

interface SidebarProps {
  selectedModule: string;
  selectedSubModule: string;
  onModuleSelect: (module: string) => void;
  onSubModuleSelect: (subModule: string) => void;
}

interface ModuleConfig {
  name: string;
  icon: string;
  subModules: string[];
}

const modules: ModuleConfig[] = [
  {
    name: '实时动态',
    icon: 'ri-dashboard-line',
    subModules: []
  },
  {
    name: '生产运营',
    icon: 'ri-ship-line',
    subModules: [
      '智能盘垛与库存管理',
      '船舶进出港辅助',
      '水尺计重系统',
      '航道与航标巡检'
    ]
  },
  {
    name: '安全应急',
    icon: 'ri-shield-check-line',
    subModules: [
      '全天候安防巡逻',
      '突发环境事件应急',
      '夜间与恶劣天气侦察',
      '三维建模与应急推演'
    ]
  },
  {
    name: '设施设备',
    icon: 'ri-building-line',
    subModules: [
      '大型设施巡检',
      '输油管道巡检',
      '高架桥巡检'
    ]
  },
  {
    name: '环境保护',
    icon: 'ri-leaf-line',
    subModules: [
      '海域油污监测',
      '粉尘排放监控'
    ]
  },
  {
    name: '数据报表',
    icon: 'ri-bar-chart-box-line',
    subModules: []
  }
];

export default function Sidebar({ selectedModule, selectedSubModule, onModuleSelect, onSubModuleSelect }: SidebarProps) {
  const [expandedModules, setExpandedModules] = useState<string[]>(['实时动态']);

  const toggleModule = (moduleName: string) => {
    if (expandedModules.includes(moduleName)) {
      setExpandedModules(expandedModules.filter(m => m !== moduleName));
    } else {
      setExpandedModules([...expandedModules, moduleName]);
    }
    onModuleSelect(moduleName);
    onSubModuleSelect('');
  };

  const handleSubModuleClick = (moduleName: string, subModuleName: string) => {
    onModuleSelect(moduleName);
    onSubModuleSelect(subModuleName);
  };

  return (
    <aside className="w-64 bg-gray-900 border-r border-teal-500/20 flex flex-col">
      {/* Logo区域 */}
      <div className="p-6 border-b border-teal-500/20">
        <div className="flex items-center gap-3">
          <img 
            src="https://static.readdy.ai/image/a68e6275e3edc3dffa3b1dce13502b4a/e4c5d2d87fb7ab856d386f6e17f08b6c.png" 
            alt="Logo" 
            className="h-10 w-10"
          />
          <div>
            <h1 className="text-white font-bold text-lg">智创灵眸</h1>
            <p className="text-teal-400 text-xs">管理平台</p>
          </div>
        </div>
      </div>

      {/* 导航菜单 */}
      <nav className="flex-1 overflow-y-auto py-4">
        {modules.map((module) => (
          <div key={module.name} className="mb-1">
            {/* 主模块 */}
            <button
              onClick={() => toggleModule(module.name)}
              className={`w-full px-6 py-3 flex items-center justify-between transition-all cursor-pointer ${
                selectedModule === module.name && !selectedSubModule
                  ? 'bg-teal-500/20 text-teal-400 border-r-2 border-teal-500'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <i className={`${module.icon} text-lg`}></i>
                <span className="text-sm font-medium">{module.name}</span>
              </div>
              {module.subModules.length > 0 && (
                <i className={`ri-arrow-${expandedModules.includes(module.name) ? 'down' : 'right'}-s-line text-sm transition-transform`}></i>
              )}
            </button>

            {/* 子模块 */}
            {module.subModules.length > 0 && expandedModules.includes(module.name) && (
              <div className="bg-gray-950/50">
                {module.subModules.map((subModule) => (
                  <button
                    key={subModule}
                    onClick={() => handleSubModuleClick(module.name, subModule)}
                    className={`w-full px-6 py-2.5 pl-14 text-left text-sm transition-all cursor-pointer ${
                      selectedModule === module.name && selectedSubModule === subModule
                        ? 'bg-teal-500/10 text-teal-400'
                        : 'text-gray-500 hover:bg-gray-800 hover:text-gray-300'
                    }`}
                  >
                    {subModule}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* 底部信息 */}
      <div className="p-4 border-t border-teal-500/20">
        <div className="bg-gray-800/50 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-xs text-gray-400">系统运行正常</span>
          </div>
          <div className="text-xs text-gray-500">
            在线无人机: <span className="text-teal-400 font-medium">24</span>
          </div>
        </div>
      </div>
    </aside>
  );
}